/**
 * imageCompression.js — Client-side image compression utility
 * ─────────────────────────────────────────────────────────────
 * Pure canvas-based, zero external dependencies.
 * Produces JPEG (or WebP when supported) output — optimised for
 * coloring photos that are typically shot on mobile.
 *
 * Two exports:
 *   compressImageForUpload(file, options?)  → File  (max 1600px, q 0.82)
 *   createImageThumbnail(file, options?)    → File  (max 500px,  q 0.78)
 *
 * Both functions:
 *  – Accept JPEG, PNG, WebP, HEIC (browser permitting).
 *  – Fall back to the original file on any error so uploads never break.
 *  – Return a File object with the same name (extension replaced to .jpg).
 */

// Detect WebP encoding support (cached after first call).
let _webpSupported = null;
function supportsWebpEncoding() {
  if (_webpSupported !== null) return _webpSupported;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1; canvas.height = 1;
    _webpSupported = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch (_) {
    _webpSupported = false;
  }
  return _webpSupported;
}

function chooseOutputMime() {
  return supportsWebpEncoding() ? 'image/webp' : 'image/jpeg';
}

function replaceExtension(fileName, mime) {
  const ext = mime === 'image/webp' ? '.webp' : '.jpg';
  return (fileName || 'image.jpg').replace(/\.[^.]+$/, '') + ext;
}

/**
 * Load a File/Blob into an HTMLImageElement.
 * Returns a Promise<HTMLImageElement>.
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

/**
 * Draw img onto a canvas respecting maxWidth/maxHeight (preserving aspect ratio),
 * then encode at the given quality and mime type.
 * Returns a Promise<Blob>.
 */
function resizeAndEncode(img, maxWidth, maxHeight, mime, quality) {
  return new Promise((resolve, reject) => {
    let { naturalWidth: w, naturalHeight: h } = img;

    // Scale down if needed, never scale up.
    const ratio = Math.min(1, maxWidth / w, maxHeight / h);
    const dw = Math.round(w * ratio);
    const dh = Math.round(h * ratio);

    const canvas = document.createElement('canvas');
    canvas.width  = dw;
    canvas.height = dh;

    const ctx = canvas.getContext('2d');
    if (!ctx) { reject(new Error('Canvas context unavailable')); return; }

    ctx.drawImage(img, 0, 0, dw, dh);

    canvas.toBlob(
      (blob) => {
        if (!blob) { reject(new Error('toBlob returned null')); return; }
        resolve(blob);
      },
      mime,
      quality,
    );
  });
}

/**
 * Core helper — compresses a file to the given dimensions + quality.
 * Returns a new File, or the original if compression fails / produces a
 * larger file (passthrough).
 */
async function compress(file, { maxWidth, maxHeight, quality, label = 'img' }) {
  try {
    const mime = chooseOutputMime();
    const img  = await loadImage(file);
    const blob = await resizeAndEncode(img, maxWidth, maxHeight, mime, quality);

    // Never return a larger file than the input.
    if (blob.size >= file.size) return file;

    const outName = replaceExtension(file.name, mime);
    return new File([blob], outName, { type: mime, lastModified: Date.now() });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[imageCompression] ${label} compression failed — using original`, err?.message);
    return file;
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Compress a coloring photo for the main upload slot.
 *
 * Defaults:
 *   maxWidth  = 1600 px
 *   maxHeight = 1600 px
 *   quality   = 0.82
 *
 * @param {File} file — original image File
 * @param {object} [options]
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file, options = {}) {
  if (!file) return file;
  return compress(file, {
    maxWidth:  options.maxWidth  ?? 1600,
    maxHeight: options.maxHeight ?? 1600,
    quality:   options.quality   ?? 0.82,
    label: 'main',
  });
}

/**
 * Create a small thumbnail of a coloring photo.
 *
 * Defaults:
 *   maxWidth  = 500 px
 *   maxHeight = 500 px
 *   quality   = 0.78
 *
 * @param {File} file — original (or already-compressed) image File
 * @param {object} [options]
 * @returns {Promise<File>}
 */
export async function createImageThumbnail(file, options = {}) {
  if (!file) return file;
  return compress(file, {
    maxWidth:  options.maxWidth  ?? 500,
    maxHeight: options.maxHeight ?? 500,
    quality:   options.quality   ?? 0.78,
    label: 'thumb',
  });
}
